import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { get, type DelaySeries, type NodeRef, type OutputPathDelay, type PathDelay, type PathSample, type Sample } from '../api'

/*
 * The path delay of a live stream on the admin Streams page (ADMIN-DELAY-1): "frame left OBS → frame
 * left for the platform" by segment, p50 of each, as the API computes it (GET /api/admin/streams,
 * path_delay; docs/latency.md §2). One output: one line. Two or more: a summary with the range of
 * totals that opens into a line per output. Yellow when a total is above 1 s; delays by design (SRT
 * TSBPD, video delay, HLS) on their own line, never added. Every number is in words, so nothing
 * depends on the colour; tooltips say what each segment measures.
 */

const ms = (v?: number) => (v === undefined ? '—' : v < 1000 ? `${Math.round(v)} ms` : `${(v / 1000).toFixed(2)} s`)
const nodeName = (n?: NodeRef) => n?.name || n?.id || 'the node'

function Seg({ label, value, title, testId }: { label: string; value?: number; title: string; testId: string }) {
  return (
    <span title={title} data-testid={testId} data-ms={value ?? ''} className="whitespace-nowrap">
      {label} <span className="text-text-soft">{ms(value)}</span>
    </span>
  )
}

/** The four segments, the total and the clock of one output. */
function Segments({ o, ingress, prefix }: { o: OutputPathDelay; ingress?: NodeRef; prefix: string }) {
  const s = o.segments
  const egress = nodeName(o.egress_node)
  const hops = s.network_hops?.map((h) => `${nodeName(h.to)} ${ms(h.cum_ms)}`).join(' → ')
  const parts = [
    <Seg
      key="in"
      label="OBS → node"
      value={s.obs_to_node_ms}
      testId={`${prefix}-in`}
      title={`Half the round trip encoder ↔ ${nodeName(ingress)} plus the encoder's backlog. An estimate: RTMP carries no wall-clock time.`}
    />,
    <Seg
      key="node"
      label="node"
      value={s.node_ms}
      testId={`${prefix}-node`}
      title={
        s.node_ms === undefined
          ? `No figure from ${egress} yet (an older node, or the output has not caught up).`
          : `Bus → platform socket on ${egress}, p50 of the last 10 s (p99 ${ms(s.node_p99_ms)}); of it waiting on the bus ${ms(s.node_bus_wait_ms)}. A video delay by design is not included.`
      }
    />,
  ]
  if (s.network_hops || s.network_ms !== undefined) {
    parts.push(
      <Seg
        key="net"
        label="network"
        value={s.network_ms}
        testId={`${prefix}-net`}
        title={`${nodeName(ingress)} took the frame off its bus → it arrived on ${egress}, p50${s.network_p99_ms !== undefined ? ` (p99 ${ms(s.network_p99_ms)})` : ''}.${hops ? ` From the ingress: ${hops}.` : ''}${
          o.check_e2e_ms !== undefined ? ` End-to-end check (network + node): ${ms(o.check_e2e_ms)}${o.e2e_mismatch ? ', more than 50 ms off the sum: the windows differ, or a clock is off' : ''}.` : ''
        }`}
      />,
    )
  }
  parts.push(
    <Seg
      key="out"
      label="→ platform"
      value={s.to_platform_ms}
      testId={`${prefix}-out`}
      title={
        o.by_design?.hls
          ? `Half the round trip to the platform (${ms(s.platform_rtt_ms)}). HLS segments wait by design and are not counted.`
          : `Kernel send queue ${ms(s.send_queue_ms)} + half the round trip to the platform (${ms(s.platform_rtt_ms)}).`
      }
    />,
  )
  return (
    <>
      {parts.flatMap((p, i) => (i ? [<span key={`d${i}`} className="text-faint"> · </span>, p] : [p]))}
      <span className="text-faint"> · </span>
      <span
        data-testid={`${prefix}-total`}
        data-ms={o.total_ms ?? ''}
        data-warning={o.warning}
        title={`The sum of the segments, p50 each; yellow above 1 s. Delays by design are not included.${o.partial ? ' A segment has no figure yet: this is what is known.' : ''}`}
        className={`whitespace-nowrap ${o.warning ? 'text-warn' : ''}`}
      >
        total <span className={o.warning ? 'text-warn' : 'text-text'}>{ms(o.total_ms)}</span>
        {o.partial ? '+' : ''}
      </span>
      {o.clock && (
        <span
          className="ml-2 whitespace-nowrap text-warn"
          data-testid={`${prefix}-clock`}
          title={`The clocks of ${nodeName(ingress)} and ${egress} differ by ${o.clock.error_ms} ms${o.clock.suspect ? ' and one of them is not in sync' : ''}: the network figure carries this error.`}
        >
          clock {o.clock.error_ms > 0 ? '+' : ''}
          {o.clock.error_ms} ms{o.clock.suspect ? ', suspect' : ''}
        </span>
      )}
      {o.e2e_mismatch && (
        <span className="ml-2 text-muted" data-testid={`${prefix}-mismatch`} title="The routing network's end-to-end figure is more than 50 ms off network + node">
          ≠ e2e {ms(o.check_e2e_ms)}
        </span>
      )}
    </>
  )
}

function designed(o: OutputPathDelay): string {
  const d = o.by_design
  if (!d) return ''
  return [d.srt_tsbpd_ms ? `SRT ${ms(d.srt_tsbpd_ms)}` : '', d.video_delay_ms ? `video delay ${ms(d.video_delay_ms)}` : '', d.hls ? 'HLS segments' : ''].filter(Boolean).join(' · ')
}

// ---- chart -------------------------------------------------------------------------------------

const W = 240
const H = 40
const LAYERS: { key: keyof PathSample; label: string; cls: string }[] = [
  { key: 'obs_to_node_ms', label: 'OBS → node', cls: 'fill-text-soft' },
  { key: 'node_ms', label: 'node', cls: 'fill-accent' },
  { key: 'network_ms', label: 'network', cls: 'fill-start' },
  { key: 'to_platform_ms', label: '→ platform', cls: 'fill-muted' },
]

type Point = PathSample & { t: string }

/** Stacked segments over time, the 1 s line dashed. */
function PathChart({ points, testId, slots }: { points: Point[]; testId: string; slots: number }) {
  const n = points.length
  if (n < 2) {
    return (
      <p className="m-0 font-mono text-[10px] text-faint" data-testid={testId} data-points={n}>
        no history for this range yet
      </p>
    )
  }
  // Scale to the data (the 1 s line only when the data comes near it), so 150 ms lines stay readable.
  const top = Math.max(200, ...points.map((p) => LAYERS.reduce((a, l) => a + ((p[l.key] as number | undefined) ?? 0), 0))) * 1.2
  const limit = top >= 1000
  const x = (i: number) => ((i + Math.max(0, slots - n)) * W) / Math.max(1, slots - 1)
  const y = (v: number) => H - 1 - (v / top) * (H - 2)
  const base = new Array(n).fill(0)
  const paths = LAYERS.map((l) => {
    const lo = [...base]
    const hi = points.map((p, i) => (base[i] += (p[l.key] as number | undefined) ?? 0))
    if (n < 2) return ''
    let d = `M${x(0).toFixed(1)} ${y(hi[0]!).toFixed(1)}`
    for (let i = 1; i < n; i++) d += `L${x(i).toFixed(1)} ${y(hi[i]!).toFixed(1)}`
    for (let i = n - 1; i >= 0; i--) d += `L${x(i).toFixed(1)} ${y(lo[i]!).toFixed(1)}`
    return d + 'Z'
  })
  const worst = Math.max(0, ...points.map((p) => p.total_max_ms ?? p.total_ms ?? 0))
  return (
    <figure className="m-0 flex flex-none flex-col gap-1" data-testid={testId} data-points={n}>
      <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} role="img" aria-label="Path delay by segment over time" className="block rounded-sm bg-bg">
        {paths.map((d, i) => (
          <path key={LAYERS[i]!.key} d={d} className={LAYERS[i]!.cls} fillOpacity={0.75} />
        ))}
        {limit && <path d={`M0 ${y(1000).toFixed(1)}H${W}`} className="stroke-warn" strokeDasharray="3 3" fill="none" />}
      </svg>
      <figcaption className="num flex flex-wrap gap-x-3 font-mono text-[10px] text-muted">
        {LAYERS.map((l) => (
          <span key={l.key} className="inline-flex items-center gap-1">
            <svg width="8" height="8" aria-hidden="true">
              <rect width="8" height="8" className={l.cls} fillOpacity={0.75} />
            </svg>
            {l.label}
          </span>
        ))}
        <span>
          worst total {ms(worst)} · scale {ms(top)}
          {limit ? ' · dashed 1 s' : ''}
        </span>
      </figcaption>
    </figure>
  )
}

const RANGES = ['5m', '1h', '24h', '7d'] as const
type Range = (typeof RANGES)[number]

/** One output's chart: the last 5 minutes from memory, longer ranges from the database. */
function OutputHistory({ id, samples }: { id: string; samples?: Sample[] }) {
  const [range, setRange] = useState<Range>('5m')
  const db = useQuery({
    queryKey: ['admin-output-delay', id, range],
    queryFn: () => get<DelaySeries>(`/api/admin/outputs/${encodeURIComponent(id)}/delay?range=${range}`),
    enabled: range !== '5m',
    refetchInterval: range === '1h' ? 15_000 : 60_000,
  })
  const points: Point[] =
    range === '5m' ? (samples ?? []).filter((s) => s.path?.total_ms !== undefined).map((s) => ({ ...s.path, t: s.t })) : (db.data?.points ?? [])
  const slots = range === '5m' ? 60 : Math.max(points.length, 2)
  return (
    <div className="flex flex-wrap items-start gap-3 pt-1">
      <PathChart points={points} testId={`path-history-${id}`} slots={slots} />
      <div className="flex gap-1" role="group" aria-label="History range">
        {RANGES.map((r) => (
          <button
            key={r}
            type="button"
            onClick={() => setRange(r)}
            data-testid={`path-range-${id}-${r}`}
            aria-pressed={r === range}
            className={`rounded-sm px-1.5 py-0.5 font-mono text-[10px] ${r === range ? 'bg-slot text-text' : 'text-muted hover:text-text-soft'}`}
          >
            {r}
          </button>
        ))}
        {range !== '5m' && db.isError && <span className="text-[10px] text-warn">history unavailable</span>}
      </div>
    </div>
  )
}

/** One output: label (when there are several), the segments, the designed delays, the chart. */
function OutputLine({ o, ingress, stream, label, samples }: { o: OutputPathDelay; ingress?: NodeRef; stream: string; label: boolean; samples?: Sample[] }) {
  const prefix = `stream-delay-${stream}-${o.output_id}`
  const d = designed(o)
  return (
    <div data-testid={prefix} className="py-0.5">
      <p className="num text-xs text-muted">
        {label && (
          <span className="mr-2 inline-block min-w-[9rem] text-text-soft" title={`${o.platform} · output ${o.output_id}${o.egress_node ? ` on ${nodeName(o.egress_node)}` : ''}`}>
            {o.label || o.platform}
          </span>
        )}{' '}
        {!label && <span>Delay </span>}
        <Segments o={o} ingress={ingress} prefix={prefix} />
      </p>
      {d && (
        <p className="num text-xs text-faint" data-testid={`${prefix}-designed`} title="Delays by design: shown apart, never in the total">
          by design: {d}
        </p>
      )}
      <OutputHistory id={o.output_id} samples={samples} />
    </div>
  )
}

export function PathDelayBlock({ stream, pd, paths }: { stream: string; pd: PathDelay; paths?: Record<string, Sample[]> }) {
  const [open, setOpen] = useState(false)
  if (pd.outputs.length === 0) {
    return (
      <p className="num text-xs text-muted" data-testid={`stream-delay-${stream}`} title="No live output yet: only OBS → node is known">
        Delay OBS → node {ms(pd.obs_to_node_ms)}
      </p>
    )
  }
  if (pd.outputs.length === 1) {
    const o = pd.outputs[0]!
    return (
      <div data-testid={`stream-delay-${stream}`}>
        <OutputLine o={o} ingress={pd.ingress_node} stream={stream} label={false} samples={paths?.[o.output_id]} />
      </div>
    )
  }
  const range = pd.total_min_ms === pd.total_max_ms ? ms(pd.total_min_ms) : `${ms(pd.total_min_ms)}–${ms(pd.total_max_ms)}`
  return (
    <div data-testid={`stream-delay-${stream}`}>
      <p className="num text-xs text-muted">
        Delay{' '}
        <span title="Half the round trip encoder ↔ node plus the encoder's backlog (estimate)">
          OBS → node <span className="text-text-soft">{ms(pd.obs_to_node_ms)}</span>
        </span>
        <span className="text-faint"> · </span>
        <span data-testid={`stream-delay-${stream}-range`} data-warning={pd.warning} className={pd.warning ? 'text-warn' : ''} title="Total per output, p50 of each segment; yellow when one is above 1 s">
          to platforms <span className={pd.warning ? 'text-warn' : 'text-text'}>{range}</span> ({pd.outputs.length} outputs)
        </span>{' '}
        <button
          type="button"
          className="text-accent hover:underline"
          aria-expanded={open}
          data-testid={`stream-delay-${stream}-toggle`}
          onClick={() => setOpen(!open)}
        >
          {open ? 'hide ▾' : 'by output ▸'}
        </button>
      </p>
      {open && (
        <div className="mt-1 border-l border-line pl-3">
          {pd.outputs.map((o) => (
            <OutputLine key={o.output_id} o={o} ingress={pd.ingress_node} stream={stream} label samples={paths?.[o.output_id]} />
          ))}
        </div>
      )}
    </div>
  )
}
